import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  getAppPermissions, 
  updateAppPermission, 
  getNextAvailablePermissionId, 
  checkPermissionIdExists, 
  addAppPermission, 
  deleteAppPermission, 
  syncAppPermission,
  getAppPortalPermissions,
  updateAppPortalPermission,
  getNextAvailablePortalPermissionId,
  checkPortalPermissionIdExists,
  addAppPortalPermission,
  deleteAppPortalPermission,
  syncAppPortalPermission
} from 'api/appManager';
import { LIST_APP_PERMISSIONS_QUERY } from 'constants/QueryString';

export const useGetAppPermissions = (appId) => {
    const { data, isLoading, refetch, error, isFetching } = useQuery({
        queryKey: [LIST_APP_PERMISSIONS_QUERY, appId],
        queryFn: async () => {
            try {
                const response = await getAppPermissions(appId);
                return response;
            } catch (error) {
                throw error;
            }
        },
        enabled: !!appId,
    });

    return {
        data,
        isLoading,
        isFetching,
        refetch,
        error,
    };
};

export const useUpdateAppPermission = () => {
    const { mutate, isPending, error, isSuccess } = useMutation({
        mutationFn: updateAppPermission,
        onError: (error) => {
            console.error('Error updating permission:', error);
        },
    });

    return {
        mutate,
        isPending,
        error,
        isSuccess,
    };
};

export const useGetNextAvailablePermissionId = () => {
    const { data, isLoading, refetch, error, isFetching } = useQuery({
        queryKey: ['nextAvailablePermissionId'],
        queryFn: async () => {
            try {
                const response = await getNextAvailablePermissionId();
                return response;
            } catch (error) {
                throw error;
            }
        },
        enabled: false, // Manual trigger
    });

    return {
        data,
        isLoading,
        isFetching,
        refetch,
        error,
    };
};

export const useCheckPermissionIdExists = () => {
    const { mutate, data, isPending, error } = useMutation({
        mutationFn: checkPermissionIdExists,
        onError: (error) => {
            console.error('Error checking permission ID:', error);
        },
    });

    return {
        mutate,
        data,
        isPending,
        error,
    };
};

export const useAddAppPermission = () => {
    const { mutate, isPending, error, isSuccess } = useMutation({
        mutationFn: addAppPermission,
        onError: (error) => {
            console.error('Error adding permission:', error);
        },
    });

    return {
        mutate,
        isPending,
        error,
        isSuccess,
    };
};

export const useDeleteAppPermission = () => {
    const { mutate, isPending, error, isSuccess } = useMutation({
        mutationFn: deleteAppPermission,
        onError: (error) => {
            console.error('Error deleting permission:', error);
        },
    });

    return {
        mutate,
        isPending,
        error,
        isSuccess,
    };
};

export const useSyncAppPermission = () => {
    const { mutate, isPending, error, isSuccess } = useMutation({
        mutationFn: syncAppPermission,
        onError: (error) => {
            console.error('Error syncing permission:', error);
        },
    });

    return {
        mutate,
        isPending,
        error,
        isSuccess,
    };
};

// Portal Permission Hooks
export const useGetAppPortalPermissions = (appId) => {
    const { data, isLoading, refetch, error, isFetching } = useQuery({
        queryKey: [LIST_APP_PERMISSIONS_QUERY + '_portal', appId],
        queryFn: async () => {
            try {
                const response = await getAppPortalPermissions(appId);
                return response;
            } catch (error) {
                throw error;
            }
        },
        enabled: !!appId,
    });

    return {
        data,
        isLoading,
        isFetching,
        refetch,
        error,
    };
};

export const useUpdateAppPortalPermission = () => {
    const { mutate, isPending, error, isSuccess } = useMutation({
        mutationFn: updateAppPortalPermission,
        onError: (error) => {
            console.error('Error updating portal permission:', error);
        },
    });

    return {
        mutate,
        isPending,
        error,
        isSuccess,
    };
};

export const useGetNextAvailablePortalPermissionId = () => {
    const { data, isLoading, refetch, error, isFetching } = useQuery({
        queryKey: ['nextAvailablePortalPermissionId'],
        queryFn: async () => {
            try {
                const response = await getNextAvailablePortalPermissionId();
                return response;
            } catch (error) {
                throw error;
            }
        },
        enabled: false, // Manual trigger
    });

    return {
        data,
        isLoading,
        isFetching,
        refetch,
        error,
    };
};

export const useCheckPortalPermissionIdExists = () => {
    const { mutate, data, isPending, error } = useMutation({
        mutationFn: checkPortalPermissionIdExists,
        onError: (error) => {
            console.error('Error checking portal permission ID:', error);
        },
    });

    return {
        mutate,
        data,
        isPending,
        error,
    };
};

export const useAddAppPortalPermission = () => {
    const { mutate, isPending, error, isSuccess } = useMutation({
        mutationFn: addAppPortalPermission,
        onError: (error) => {
            console.error('Error adding portal permission:', error);
        },
    });

    return {
        mutate,
        isPending,
        error,
        isSuccess,
    };
};

export const useDeleteAppPortalPermission = () => {
    const { mutate, isPending, error, isSuccess } = useMutation({
        mutationFn: deleteAppPortalPermission,
        onError: (error) => {
            console.error('Error deleting portal permission:', error);
        },
    });

    return {
        mutate,
        isPending,
        error,
        isSuccess,
    };
};

export const useSyncAppPortalPermission = () => {
    const { mutate, isPending, error, isSuccess } = useMutation({
        mutationFn: syncAppPortalPermission,
        onError: (error) => {
            console.error('Error syncing portal permission:', error);
        },
    });

    return {
        mutate,
        isPending,
        error,
        isSuccess,
    };
};
